import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { UsageData } from "@/hooks/useUsage";

const subscriptionCardVariants = cva(
  "relative h-full overflow-hidden transition-all duration-300 ease-out border-0 shadow-lg group backdrop-blur-xl hover:shadow-xl hover:-translate-y-1",
  {
    variants: {
      variant: {
        max: "bg-gradient-to-br from-emerald-50 via-green-50/50 to-emerald-50 dark:from-emerald-950/30 dark:via-green-950/20 dark:to-emerald-950/30 shadow-emerald-900/5 dark:shadow-emerald-900/20 hover:shadow-emerald-900/10 dark:hover:shadow-emerald-900/30",
        pro: "bg-gradient-to-br from-blue-50 via-indigo-50/50 to-blue-50 dark:from-blue-950/30 dark:via-indigo-950/20 dark:to-blue-950/30 shadow-blue-900/5 dark:shadow-blue-900/20 hover:shadow-blue-900/10 dark:hover:shadow-blue-900/30",
        hobby:
          "bg-gradient-to-br from-yellow-50 via-amber-50/50 to-yellow-50 dark:from-yellow-950/30 dark:via-amber-950/20 dark:to-yellow-950/30 shadow-yellow-900/5 dark:shadow-yellow-900/20 hover:shadow-yellow-900/10 dark:hover:shadow-yellow-900/30",
        free: "bg-gradient-to-br from-slate-50 via-gray-50/50 to-slate-50 dark:from-slate-950/30 dark:via-gray-950/20 dark:to-slate-950/30 shadow-slate-900/5 dark:shadow-slate-900/20 hover:shadow-slate-900/10 dark:hover:shadow-slate-900/30",
      },
    },
    defaultVariants: {
      variant: "free",
    },
  },
);

const accentVariants = cva("absolute top-0 left-0 w-full h-1", {
  variants: {
    variant: {
      max: "bg-gradient-to-r from-emerald-400 to-green-400",
      pro: "bg-gradient-to-r from-blue-400 to-indigo-400",
      hobby: "bg-gradient-to-r from-yellow-400 to-amber-400",
      free: "bg-gradient-to-r from-slate-400 to-gray-400",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const patternVariants = cva("absolute inset-0 bg-[size:12px_12px]", {
  variants: {
    variant: {
      max: "bg-[linear-gradient(to_right,#10b98108_1px,transparent_1px),linear-gradient(to_bottom,#10b98108_1px,transparent_1px)]",
      pro: "bg-[linear-gradient(to_right,#3b82f608_1px,transparent_1px),linear-gradient(to_bottom,#3b82f608_1px,transparent_1px)]",
      hobby:
        "bg-[linear-gradient(to_right,#f59e0b08_1px,transparent_1px),linear-gradient(to_bottom,#f59e0b08_1px,transparent_1px)]",
      free: "bg-[linear-gradient(to_right,#64748b08_1px,transparent_1px),linear-gradient(to_bottom,#64748b08_1px,transparent_1px)]",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const titleVariants = cva("text-xl font-semibold tracking-tight", {
  variants: {
    variant: {
      max: "text-emerald-900 dark:text-emerald-100",
      pro: "text-blue-900 dark:text-blue-100",
      hobby: "text-yellow-900 dark:text-yellow-100",
      free: "text-slate-900 dark:text-slate-100",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const badgeVariants = cva("px-2 py-0.5 text-xs font-medium rounded-full", {
  variants: {
    variant: {
      max: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
      pro: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300",
      hobby:
        "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300",
      free: "bg-slate-100 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const descriptionVariants = cva("text-xs font-medium tracking-wide uppercase", {
  variants: {
    variant: {
      max: "text-emerald-600 dark:text-emerald-400",
      pro: "text-blue-600 dark:text-blue-400",
      hobby: "text-yellow-600 dark:text-yellow-400",
      free: "text-slate-600 dark:text-slate-400",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const priceVariants = cva("text-xs font-semibold", {
  variants: {
    variant: {
      max: "text-emerald-800 dark:text-emerald-200",
      pro: "text-blue-800 dark:text-blue-200",
      hobby: "text-yellow-800 dark:text-yellow-200",
      free: "text-slate-800 dark:text-slate-200",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const iconContainerVariants = cva(
  "p-2.5 rounded-2xl transition-all duration-300",
  {
    variants: {
      variant: {
        max: "bg-emerald-100 dark:bg-emerald-900/40 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-900/60",
        pro: "bg-blue-100 dark:bg-blue-900/40 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/60",
        hobby:
          "bg-yellow-100 dark:bg-yellow-900/40 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-900/60",
        free: "bg-slate-100 dark:bg-slate-900/40 group-hover:bg-slate-200 dark:group-hover:bg-slate-900/60",
      },
    },
    defaultVariants: {
      variant: "free",
    },
  },
);

const iconVariants = cva("w-5 h-5", {
  variants: {
    variant: {
      max: "text-emerald-600 dark:text-emerald-400",
      pro: "text-blue-600 dark:text-blue-400",
      hobby: "text-yellow-600 dark:text-yellow-400",
      free: "text-slate-600 dark:text-slate-400",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const progressVariants = cva("h-2 rounded-full transition-all duration-500", {
  variants: {
    variant: {
      max: "bg-emerald-200 dark:bg-emerald-900/40",
      pro: "bg-blue-200 dark:bg-blue-900/40",
      hobby: "bg-yellow-200 dark:bg-yellow-900/40",
      free: "bg-slate-200 dark:bg-slate-900/40",
    },
  },
  defaultVariants: {
    variant: "free",
  },
});

const progressFillVariants = cva("h-full rounded-full transition-all duration-700 ease-out", {
  variants: {
    variant: {
      max: "bg-gradient-to-r from-emerald-500 to-green-500",
      pro: "bg-gradient-to-r from-blue-500 to-indigo-500",
      hobby: "bg-gradient-to-r from-yellow-500 to-amber-500",
      free: "bg-gradient-to-r from-slate-500 to-gray-500",
    },
    status: {
      healthy: "",
      warning: "from-yellow-500 to-orange-500",
      critical: "from-red-500 to-red-600",
    },
  },
  defaultVariants: {
    variant: "free",
    status: "healthy",
  },
});

interface SubscriptionCardProps
  extends VariantProps<typeof subscriptionCardVariants> {
  planName: string;
  price: number | string;
  period: string;
  hasActivePlan?: boolean;
  isLoading?: boolean;
  usage?: UsageData | null;
  usageLoading?: boolean;
}

const getPlanVariant = (planName: string): "max" | "pro" | "hobby" | "free" => {
  switch (planName.toLowerCase()) {
    case "max":
      return "max";
    case "pro":
      return "pro";
    case "hobby":
      return "hobby";
    default:
      return "free";
  }
};

const formatResetTime = (resetIn: number | null): string => {
  if (!resetIn) return "Unknown";
  
  const hours = Math.floor(resetIn / 3600);
  const minutes = Math.floor((resetIn % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const getUsageStatus = (current: number, limit: number): "healthy" | "warning" | "critical" => {
  const percentage = (current / limit) * 100;
  if (percentage >= 85) return "critical";
  if (percentage >= 60) return "warning";
  return "healthy";
};

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  planName,
  price,
  period,
  hasActivePlan = false,
  isLoading = false,
  usage = null,
  usageLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className="relative h-full overflow-hidden transition-all duration-300 ease-out border-0 shadow-lg bg-gradient-to-br from-slate-50 via-gray-50/50 to-slate-50 dark:from-slate-950/30 dark:via-gray-950/20 dark:to-slate-950/30 backdrop-blur-xl shadow-slate-900/5 dark:shadow-slate-900/20">
        <CardContent className="relative p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-2">
              <div className="w-20 h-6 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
              <div className="space-y-1">
                <div className="w-16 h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
                <div className="w-24 h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const variant = getPlanVariant(planName);
  
  // Usage calculations
  const usagePercentage = usage ? (usage.usage.current / usage.usage.limit) * 100 : 0;
  const usageStatus = usage ? getUsageStatus(usage.usage.current, usage.usage.limit) : "healthy";

  return (
    <Card className={subscriptionCardVariants({ variant })}>
      {/* Premium gradient accent */}
      <div className={accentVariants({ variant })} />

      {/* Subtle background pattern */}
      <div className={patternVariants({ variant })} />

      <CardContent className="relative p-5 space-y-4">
        {/* Plan Information Section */}
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <p className={titleVariants({ variant })}>{planName} Plan</p>
              {hasActivePlan && (
                <span className={badgeVariants({ variant })}>ACTIVE</span>
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-1">
                <span className={priceVariants({ variant })}>
                  ${price}/{period}
                </span>
              </div>
            </div>
          </div>
          <div className={iconContainerVariants({ variant })}>
            <Sparkles className={iconVariants({ variant })} />
          </div>
        </div>

        {/* Usage Information Section */}
        {usage && !usageLoading && (
          <div className="space-y-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
            {/* Usage Stats with Reset Time */}
            <div className="flex items-center justify-between">
              <span className={`text-sm font-semibold ${priceVariants({ variant })}`}>
                {usage.usage.current}/{usage.usage.limit} requests
              </span>
              {usage.usage.resetIn && (
                <span className={`text-xs ${descriptionVariants({ variant })}`}>
                  [ Resets in {formatResetTime(usage.usage.resetIn)} ]
                </span>
              )}
            </div>

            {/* Progress Bar */}
            <div className={progressVariants({ variant })}>
              <div
                className={progressFillVariants({ variant, status: usageStatus })}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Usage Loading State */}
        {usageLoading && (
          <div className="space-y-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
            <div className="flex items-center justify-between">
              <div className="w-16 h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
              <div className="w-20 h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
